// New Season — sensor editing rules, ported from production's Edit Field
// (dashboard/field/SensorsWrapper.jsx + EditSensor.jsx + Sensor/Sensor.jsx).
//
// The season editor must write sensors exactly as Edit Field does, so every
// rule here is a line-for-line port; when production changes, change this
// too. Kept free of React and webpack aliases (the sensor classes are passed
// in) so it runs under `node --test`.
//
// A "draft" is EditSensor's local state (chosenType, chosenTransmitterID, …)
// under shorter names. Functions that take a sensor mutate it, as production
// does; callers pass a clone (see cloneSensor).
const {
  TENSIOMETER_SUB_TYPES,
  tensiometerRangeFromSubType,
  persistedTensiometerSubType,
} = require('../dashboard/field/tensiometerRangeHelpers');
const {
  isHighPressureBoundariesInvalid,
} = require('../dashboard/field/pressureBoundariesValidation');
const {
  FieldModalMenuOptions,
  FarmingUnitModalMenuOptions,
} = require('../../resources/constants/menuOptions');

const SENSOR_TYPE_OPTIONS = FieldModalMenuOptions;
// EditSensor with `farmingUnitModal` (the farming unit's own sensors).
const FARMING_UNIT_SENSOR_TYPE_OPTIONS = FarmingUnitModalMenuOptions;

// EditSensor: types whose editor type is the sensor's `type` itself.
const TYPES_KEPT_AS_IS = [
  'pulse',
  'piezometer',
  'wp',
  'soil_temp',
  'high_pressure',
  'low_pressure',
];

const VALVE_TYPES = ['motorola', 'tal-gil', 'galcon', 'aquastride'];
const OFFSET_TYPES = ['piezometer', 'high_pressure', 'low_pressure'];

// Sensor.jsx: names of the WFR manufacturers in the sensors list.
const MANUFACTURER_TITLES = {
  motorola: 'Motorola',
  ndrip: 'N-Drip Flow Meter',
  galcon: 'Galcon',
  aquastride: 'Aquastride',
  'tal-gil': 'Tal Gil',
  soil_temp: 'Soil Temp Sensor',
  'ndrip-ai': 'AI Irrigator',
};

const TYPE_TITLES = {
  wp: 'N-Drip Tensiometer',
  pulse: 'N-Drip Pulse',
  piezometer: 'N-Drip Piezometer',
  soil_temp: 'Soil Temp Sensor',
  high_pressure: 'N-Drip High Pressure',
  low_pressure: 'N-Drip Low Pressure',
};

// Sensor.jsx's "Type" column.
const sensorTypeTitle = (sensor) =>
  TYPE_TITLES[sensor?.type] ||
  MANUFACTURER_TITLES[sensor?.manufacturer] + (sensor?.sub_type || '');

// A copy that keeps the sensor's class: fieldManager.sensorObjectsToDictionary
// decides what to send with `instanceof`.
const cloneSensor = (sensor) =>
  Object.assign(
    Object.create(Object.getPrototypeOf(sensor)),
    structuredClone({ ...sensor }),
  );

const cloneSensors = (sensors) =>
  Object.keys(sensors || {}).reduce((copy, id) => {
    copy[id] = cloneSensor(sensors[id]);
    return copy;
  }, {});

// UpdateFieldModal: the field's sensors as sensor objects.
const parseSensors = (sensorsJson, classes) =>
  Object.keys(sensorsJson || {}).reduce((sensors, id) => {
    const sensor = classes.sensorFromJson(structuredClone(sensorsJson[id]), id);
    sensors[sensor.id] = sensor;
    return sensors;
  }, {});

// GeneralClasses.FarmingUnit: only Tal Gil and pulse sensors are kept.
const parseFarmingUnitSensors = (sensorsJson, classes) =>
  Object.keys(sensorsJson || {}).reduce((sensors, id) => {
    const json = sensorsJson[id];
    const type = json?.type?.toLowerCase();
    let sensor;
    if (type === 'wfr' && json?.manufacturer?.toLowerCase() === 'tal-gil') {
      sensor = new classes.WFRSensorTalgil(structuredClone(json), id);
    } else if (type === 'pulse') {
      sensor = new classes.WFRSensorNDripPulse(structuredClone(json), id);
    }
    if (sensor) {
      sensors[sensor.id] = sensor;
    }
    return sensors;
  }, {});

// SensorsWrapper's mount effect: a Tal Gil sensor that is one of the farming
// unit's takes that sensor's fertilizer meters, name and location.
const linkFarmingUnitSensors = (sensors, farmingUnitSensors) => {
  if (!farmingUnitSensors) {
    return sensors;
  }
  Object.keys(sensors).forEach((sensorId) => {
    if (sensors[sensorId].manufacturer !== 'tal-gil') {
      return;
    }
    const match = Object.values(farmingUnitSensors).find(
      (farmingUnitSensor) =>
        farmingUnitSensor.transmitter_id === sensors[sensorId].transmitter_id,
    );
    if (match) {
      sensors[sensorId].fert_meters = match.fert_meters;
      sensors[sensorId].farming_unit_serial_number = match.id;
      sensors[sensorId].name = match.name;
      sensors[sensorId].coordinates = match.coordinates;
    }
  });
  return sensors;
};

// The season's sensors as Edit Field holds them once it has opened.
const sensorsForEditing = (sensorsJson, farmingUnitSensorsJson, classes) =>
  linkFarmingUnitSensors(
    parseSensors(sensorsJson, classes),
    parseFarmingUnitSensors(farmingUnitSensorsJson, classes),
  );

// EditSensor's `initialType`, for an existing sensor (never the farming unit
// modal here).
const editorTypeOf = (sensor) => {
  if (TYPES_KEPT_AS_IS.includes(sensor.type)) {
    return { type: sensor.type };
  }
  if (sensor.sub_type) {
    return { type: 'ndrip wm', subType: sensor.sub_type };
  }
  return { type: sensor.manufacturer || 'aquastride' };
};

// EditSensor's state when a type is chosen (changeTypeHandler) — also what
// "Add new" opens on.
const emptyDraft = (chosenType, defaultBoundaries) => ({
  chosenType,
  transmitterId: '',
  talGilTransmitterId: '',
  latitude: '',
  longitude: '',
  valves: [],
  m3PerPulse: '',
  name: '',
  offset: 0,
  installationDepth: 0,
  pressureMin: defaultBoundaries.min,
  pressureMax: defaultBoundaries.max,
  wpSubType: TENSIOMETER_SUB_TYPES.FIFTY_CBAR,
  fertMeters: {},
});

const newSensorDraft = (defaultBoundaries) =>
  emptyDraft({ type: 'aquastride' }, defaultBoundaries);

// EditSensor's `initialType` for "Add new" in the farming unit modal.
const newFarmingUnitSensorDraft = (defaultBoundaries) =>
  emptyDraft({ type: 'tal-gil' }, defaultBoundaries);

// EditSensor's initial state for an existing sensor.
const draftFromSensor = (sensor, defaultBoundaries) => ({
  chosenType: editorTypeOf(sensor),
  transmitterId: sensor.transmitter_id || '',
  talGilTransmitterId: sensor.farming_unit_serial_number || '',
  latitude: sensor.coordinates?.latitude || '',
  longitude: sensor.coordinates?.longitude || '',
  valves: sensor.valves,
  m3PerPulse: sensor.m3_per_pulse,
  name: sensor.name || '',
  offset: sensor.offset || 0,
  installationDepth: sensor.installation_depth || 0,
  pressureMin: sensor.boundaries?.min ?? defaultBoundaries.min,
  pressureMax: sensor.boundaries?.max ?? defaultBoundaries.max,
  wpSubType: tensiometerRangeFromSubType(sensor.sub_type),
  fertMeters: structuredClone(sensor.fert_meters || {}),
});

// EditSensor's middle column: the one type-specific value.
const MIDDLE_FIELDS = {
  VALVES: 'valves',
  M3_PER_PULSE: 'm3_per_pulse',
  OFFSET: 'offset',
  INSTALLATION_DEPTH: 'installation_depth',
  TENSIOMETER_RANGE: 'tensiometer_range',
};

// The farming unit modal shows no valves (EditSensor's `!farmingUnitModal`).
const middleFieldOf = (chosenType, showTensiometerRange, isFarmingUnit) => {
  const type = chosenType?.type;
  if (VALVE_TYPES.includes(type) && !isFarmingUnit) {
    return MIDDLE_FIELDS.VALVES;
  }
  if (type === 'pulse') {
    return MIDDLE_FIELDS.M3_PER_PULSE;
  }
  if (OFFSET_TYPES.includes(type)) {
    return MIDDLE_FIELDS.OFFSET;
  }
  if (type === 'soil_temp') {
    return MIDDLE_FIELDS.INSTALLATION_DEPTH;
  }
  if (type === 'wp' && showTensiometerRange) {
    return MIDDLE_FIELDS.TENSIOMETER_RANGE;
  }
  return null;
};

// AI Irrigator: only the type selector is shown.
const isAIIrrigator = (draft) => draft.chosenType?.type === 'ndrip-ai';

const hasPressureBoundaries = (draft) =>
  draft.chosenType?.type === 'high_pressure';

// Name and location come from the farming unit's sensor.
const isLinkedToFarmingUnit = (draft) =>
  draft.chosenType?.type === 'tal-gil' && Boolean(draft.talGilTransmitterId);

// Max must be greater than Min (EditSensor disables Done otherwise).
const isDraftInvalid = (draft) =>
  hasPressureBoundaries(draft) &&
  isHighPressureBoundariesInvalid(draft.pressureMax, draft.pressureMin);

// The values EditSensor hands to onDone.
const doneValues = (draft) => ({
  transmitterId: draft.transmitterId,
  latitude: draft.latitude,
  longitude: draft.longitude,
  m3PerPulse: draft.m3PerPulse,
  valves: draft.valves,
  type:
    draft.chosenType.type === 'wp'
      ? {
          ...draft.chosenType,
          subType: persistedTensiometerSubType(draft.wpSubType),
        }
      : draft.chosenType,
  name: draft.name,
  offset: draft.offset,
  fertMeters: draft.fertMeters,
  installationDepth: draft.installationDepth,
  boundaries: hasPressureBoundaries(draft)
    ? {
        min: parseFloat(draft.pressureMin) || 0,
        max: parseFloat(draft.pressureMax) || 0,
      }
    : undefined,
});

// SensorsWrapper.editSensor.
const applyDraft = (sensor, draft) => {
  const values = doneValues(draft);
  sensor.transmitter_id = values.transmitterId;
  sensor.coordinates = sensor.coordinates || {};
  sensor.coordinates.latitude = values.latitude;
  sensor.coordinates.longitude = values.longitude;
  sensor.name = values.name;
  if (values.fertMeters) {
    sensor.fert_meters = values.fertMeters;
  }
  if (values.valves?.length) {
    sensor.valves = values.valves;
  } else if (values.m3PerPulse) {
    sensor.m3_per_pulse = values.m3PerPulse;
  } else if (values.offset) {
    sensor.offset = values.offset;
  }
  if (values.installationDepth) {
    sensor.installation_depth = values.installationDepth;
  }
  if (sensor.type === 'high_pressure' && values.boundaries) {
    sensor.boundaries = values.boundaries;
  }
  if (sensor.type === 'wp') {
    sensor.sub_type = values.type?.subType || '';
  }
  return sensor;
};

// SensorsWrapper's hasExistingWFRSource: pressure sensors and piezometers
// defer to whichever was added first, and to Tal Gil WFR sensors.
const hasExistingWFRSource = (sensors) =>
  Object.values(sensors || {}).some(
    (sensor) =>
      (OFFSET_TYPES.includes(sensor?.type) && sensor?.should_consider_as_wfr) ||
      (sensor?.type === 'wfr' && sensor?.manufacturer === 'tal-gil'),
  );

const NEW_SENSOR_SETTINGS = () => ({
  ignore_in_calculations: false,
  paused: false,
});

// SensorsWrapper.addSensor: {id, sensor}, or null when nothing would be
// added (no transmitter id, except for the AI Irrigator).
const newSensorFromDraft = (draft, existingSensors, classes) => {
  const values = doneValues(draft);
  const { type, subType } = values.type;
  const transmitterId = values.transmitterId;
  if (!transmitterId && type !== 'ndrip-ai') {
    return null;
  }
  const coordinates =
    !values.latitude || !values.longitude
      ? {}
      : { latitude: values.latitude, longitude: values.longitude };
  const id = type === 'ndrip-ai' ? 'tmp_ndrip_ai' : 'tmp_' + transmitterId;
  const base = {
    coordinates,
    name: values.name || '',
    transmitter_id: transmitterId,
    settings: NEW_SENSOR_SETTINGS(),
  };
  const valves = values.valves || [];
  const offset = values.offset || 0;
  const wfrSource = !hasExistingWFRSource(existingSensors);
  const builders = {
    wp: () => new classes.WPSensor({ ...base, sub_type: subType || '' }, id),
    'tal-gil': () =>
      new classes.WFRSensorTalgil(
        {
          ...base,
          valves,
          manufacturer: type,
          programs: [],
          fert_meters: values.fertMeters,
        },
        id,
      ),
    motorola: () =>
      new classes.WFRSensorMotorola(
        { ...base, valves, manufacturer: type, programs: [] },
        id,
      ),
    galcon: () =>
      new classes.WFRSensorGalcon(
        { ...base, valves, manufacturer: type, programs: [] },
        id,
      ),
    aquastride: () =>
      new classes.WFRSensorAquastride(
        { ...base, valves, manufacturer: type, programs: [] },
        id,
      ),
    'ndrip wm': () =>
      new classes.WFRSensorNDrip(
        {
          ...base,
          valves,
          manufacturer: 'ndrip',
          sub_type: subType,
          programs: [],
        },
        id,
      ),
    pulse: () =>
      new classes.WFRSensorNDripPulse(
        {
          ...base,
          m3_per_pulse: values.m3PerPulse || '',
          valves,
          manufacturer: 'ndrip',
        },
        id,
      ),
    piezometer: () =>
      new classes.NDripPiezometer(
        {
          ...base,
          should_consider_as_wfr: wfrSource,
          valves,
          offset,
          manufacturer: 'ndrip',
        },
        id,
      ),
    soil_temp: () =>
      new classes.SoilTempSensor(
        {
          ...base,
          manufacturer: 'ndrip',
          installation_depth: values.installationDepth,
        },
        id,
      ),
    high_pressure: () =>
      new classes.NDripHighPressureSensor(
        {
          ...base,
          manufacturer: 'ndrip',
          offset,
          should_consider_as_wfr: wfrSource,
          boundaries: values.boundaries || {
            ...classes.DEFAULT_HIGH_PRESSURE_BOUNDARIES,
          },
        },
        id,
      ),
    low_pressure: () =>
      new classes.NDripLowPressureSensor(
        {
          ...base,
          manufacturer: 'ndrip',
          offset,
          should_consider_as_wfr: wfrSource,
        },
        id,
      ),
    'ndrip-ai': () =>
      new classes.WFRSensorNDripAI(
        {
          transmitter_id: '',
          settings: NEW_SENSOR_SETTINGS(),
          manufacturer: 'ndrip-ai',
        },
        id,
      ),
  };
  const build = builders[type];
  return build ? { id, sensor: build() } : null;
};

// SensorsWrapper's row-menu actions.
const setIgnoreInCalculations = (sensor, value) => {
  sensor.ignore_in_calculations = value;
  sensor.settings = sensor.settings || {};
  sensor.settings.ignore_in_calculations = value;
  return sensor;
};

const setPaused = (sensor, value) => {
  sensor.paused = value;
  sensor.settings = sensor.settings || {};
  sensor.settings.paused = value;
  return sensor;
};

const setConsiderAsWFR = (sensor, value) => {
  sensor.should_consider_as_wfr = value;
  return sensor;
};

// Sensor.jsx: only pressure sensors and piezometers can be the flow meter.
const canConsiderAsFlowMeter = (type) => OFFSET_TYPES.includes(type);

// SensorsWrapper.locateSensor: the browser location, cut to 10 characters.
const locatedCoordinates = (position) => ({
  latitude: position.latitude.toString().substring(0, 10),
  longitude: position.longitude.toString().substring(0, 10),
});

// EditSensor.onNewTalGilFieldSensor: link to one of the farming unit's
// sensors.
const linkDraftToFarmingUnitSensor = (draft, farmingUnitSensor, serial) => ({
  ...draft,
  fertMeters: structuredClone(farmingUnitSensor.fert_meters || {}),
  transmitterId: farmingUnitSensor.transmitter_id,
  latitude: farmingUnitSensor.coordinates?.latitude || '',
  longitude: farmingUnitSensor.coordinates?.longitude || '',
  name: farmingUnitSensor.name,
  talGilTransmitterId: serial,
});

// EditSensor's fertilizer-row handlers. fertMeters is
// {[meterId]: [{type, start_date}]}.
const removeFertRow = (fertMeters, meterId, index) => {
  const next = structuredClone(fertMeters);
  next[meterId].splice(index, 1);
  return next;
};

const setFertRowStartDate = (fertMeters, meterId, index, unixDate) => {
  const next = structuredClone(fertMeters);
  next[meterId][index].start_date = unixDate;
  return next;
};

const setFertRowType = (fertMeters, meterId, index, type) => {
  const next = structuredClone(fertMeters);
  next[meterId][index].type = type;
  return next;
};

const setFertRowMeterId = (fertMeters, meterId, index, newMeterId) => {
  const next = structuredClone(fertMeters);
  if (next[newMeterId]) {
    next[newMeterId].push(next[meterId][index]);
    next[meterId].splice(index, 1);
  } else {
    next[newMeterId] = [{ ...next[meterId][index] }];
    next[meterId].splice(index, 1);
    if (next[meterId].length === 0) {
      delete next[meterId];
    }
  }
  return next;
};

// EditSensor.onAddFertilizerClick: one new row under an empty meter id, and
// none while that row is still there.
const addFertRow = (fertMeters) => {
  if (fertMeters?.['']) {
    return fertMeters;
  }
  return {
    ...structuredClone(fertMeters || {}),
    '': [{ type: 'no3', start_date: '' }],
  };
};

// EditSensor shows "Add fertilizer" only in the farming unit modal, for a
// Tal Gil sensor that is not linked to one of the farming unit's.
const canAddFertilizer = (draft, isFarmingUnit) =>
  isFarmingUnit &&
  draft.chosenType?.type === 'tal-gil' &&
  !draft.talGilTransmitterId;

// EditSensor: spaces and Enter are refused, the id is lower-cased.
const normalizeTransmitterId = (value) =>
  value.replace(/\s/g, '').toLowerCase();

module.exports = {
  SENSOR_TYPE_OPTIONS,
  FARMING_UNIT_SENSOR_TYPE_OPTIONS,
  newFarmingUnitSensorDraft,
  addFertRow,
  canAddFertilizer,
  MIDDLE_FIELDS,
  sensorTypeTitle,
  cloneSensor,
  cloneSensors,
  parseSensors,
  parseFarmingUnitSensors,
  linkFarmingUnitSensors,
  sensorsForEditing,
  editorTypeOf,
  emptyDraft,
  newSensorDraft,
  draftFromSensor,
  middleFieldOf,
  isAIIrrigator,
  hasPressureBoundaries,
  isLinkedToFarmingUnit,
  isDraftInvalid,
  applyDraft,
  hasExistingWFRSource,
  newSensorFromDraft,
  setIgnoreInCalculations,
  setPaused,
  setConsiderAsWFR,
  canConsiderAsFlowMeter,
  locatedCoordinates,
  linkDraftToFarmingUnitSensor,
  removeFertRow,
  setFertRowStartDate,
  setFertRowType,
  setFertRowMeterId,
  normalizeTransmitterId,
};
